import React, { useState } from 'react';
import { Col, Row } from '../../../lib/utils';
import { Body3Light, Body3Regular } from '../../../lib/font';
import { colors } from '../../../lib/colors';
import { useAccessMode } from '../../../context/AccessModeContext';
import { useSettlementCategories } from '../../../features/settlementClassification';
import {
    buildSettlementCategoryOptions,
    shouldShowCreateCategoryEntry,
    shouldShowSettlementCategoryField,
} from './settlementCategoryOptions';
import ModalSettlementCategoryManage from './ModalSettlementCategoryManage';

type Props = {
    centerId: string;
    settlementType?: string;
    value: string | null;
    onChange: (value: string | null) => void;
};

const SettlementCategoryField = ({ centerId, settlementType, value, onChange }: Props) => {
    const { isReadOnly } = useAccessMode();
    const [isManageOpen, setIsManageOpen] = useState(false);
    const { data } = useSettlementCategories({ centerId });
    const available = !!data?.available;
    const categories = data?.categories ?? [];

    const activeCategoryCount = categories.filter((c) => !c.archived).length;
    if (!shouldShowSettlementCategoryField({ available, settlementType, currentKey: value, activeCategoryCount })) {
        if (!shouldShowCreateCategoryEntry({ available, settlementType, currentKey: value, activeCategoryCount, isReadOnly })) {
            return null;
        }
        // 상세 정산 센터인데 분류가 0개 — 칸 대신 첫 분류를 만들 입구 한 줄.
        return (
            <Col style={{ width: 600, marginTop: 40, marginLeft: 80 }}>
                <Row style={{ alignItems: 'center', gap: 8 }}>
                    <button
                        type="button"
                        onClick={() => setIsManageOpen(true)}
                        className="bg-transparent border-0 p-0 cursor-pointer hover:underline"
                    >
                        <Body3Regular style={{ color: colors.primary, fontSize: 15 }}>{'+ 정산 분류 만들기'}</Body3Regular>
                    </button>
                    <Body3Light style={{ color: colors.LAVEL_1, fontSize: 14 }}>
                        {'상품마다 강사 예외 수업료를 다르게 주려면 먼저 분류를 만들어요.'}
                    </Body3Light>
                </Row>
                {isManageOpen && (
                    <ModalSettlementCategoryManage
                        centerId={centerId}
                        categories={categories}
                        onClose={() => setIsManageOpen(false)}
                    />
                )}
            </Col>
        );
    }

    const options = buildSettlementCategoryOptions(categories, value);

    return (
        <Col style={{ width: 600, marginTop: 56, marginLeft: 80 }}>
            <Body3Regular style={{ color: colors.LAVEL_4 }}>{'정산 분류'}</Body3Regular>
            <Row style={{ alignItems: 'center', marginTop: 8 }}>
                <select
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value ? e.target.value : null)}
                    style={{
                        width: 360,
                        height: 48,
                        border: 'none',
                        borderBottom: '1px solid #A6BFEF',
                        paddingLeft: 12,
                        fontSize: 18,
                        fontWeight: 400,
                        background: 'transparent',
                        outline: 'none',
                        color: value ? '#2D3642' : '#9CA3AF',
                    }}
                >
                    <option value="">없음</option>
                    {options.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                {!isReadOnly && (
                    <button
                        type="button"
                        onClick={() => setIsManageOpen(true)}
                        className="ml-[14px] bg-transparent border-0 p-0 cursor-pointer hover:underline"
                    >
                        <Body3Regular style={{ color: colors.primary, fontSize: 15 }}>{'분류 관리'}</Body3Regular>
                    </button>
                )}
            </Row>
            <Body3Light style={{ color: colors.LAVEL_1, marginTop: 10, lineHeight: '22px' }}>
                {'강사 예외 수업료에 쓰는 분류예요. 바꾸면 이미 판매한 수강권에도 적용할지 저장할 때 물어봐요.'}
            </Body3Light>
            {isManageOpen && (
                <ModalSettlementCategoryManage
                    centerId={centerId}
                    categories={categories}
                    onClose={() => setIsManageOpen(false)}
                />
            )}
        </Col>
    );
};

export default SettlementCategoryField;
